import { assembleImport } from "../ics/assemble.js";
import { parseIcsDate } from "../ics/parse.js";
import type { AssembleOptions, IcsDateValue, ImportedItem } from "../ics/types.js";
import { isoString, systemClock } from "../time.js";
import type { Status } from "../tasks/types.js";
import { colorIdToHex } from "./color.js";
import { parseLegacyExtras, parseManagedBlock, parseTaskState, parseTaskTitle } from "./task-encoding.js";
import type {
  GoogleEvent,
  GoogleEventDateTime,
  GoogleMapOptions,
  GoogleMapResult,
  LooseInstance,
  TaskExtrasEntry,
} from "./types.js";

/**
 * Map a batch of Google Calendar events to local changes. Pure (no IO): the
 * caller (store) decides create vs update vs tombstone against existing local
 * rows keyed by `external_id`.
 *
 * Reuses the ICS {@link assembleImport} engine — a Google series (master +
 * instance exceptions) maps onto the same UID-group → task + rule + overrides
 * shape as an .ics import. Because incremental deltas can deliver an instance
 * exception WITHOUT its master, instances whose master isn't in the batch are
 * returned separately as {@link LooseInstance}s for the store to attach.
 */
export function mapGoogleEvents(events: GoogleEvent[], opts: GoogleMapOptions): GoogleMapResult {
  const now = opts.now ?? systemClock;
  const tombstones: string[] = [];
  const looseInstances: LooseInstance[] = [];

  // Group by series: a master has id === seriesKey; its instances point back via
  // recurringEventId. Non-recurring events are their own singleton series.
  const groups = new Map<string, GoogleEvent[]>();
  for (const ev of events) {
    // A deleted top-level event → tombstone (matched locally by external_id).
    if (ev.status === "cancelled" && !ev.recurringEventId) {
      tombstones.push(ev.id);
      continue;
    }
    const key = ev.recurringEventId ?? ev.id;
    const g = groups.get(key);
    if (g) g.push(ev);
    else groups.set(key, [ev]);
  }

  const items: ImportedItem[] = [];
  // seriesUid (master iCalUID) → master event, for stamping external identity.
  const masterByUid = new Map<string, GoogleEvent>();

  for (const group of groups.values()) {
    const master = group.find((e) => !e.recurringEventId);
    if (!master) {
      // Lone instance exception(s): attach to the existing local series.
      for (const inst of group) looseInstances.push(toLooseInstance(inst));
      continue;
    }
    const seriesUid = master.iCalUID ?? master.id;
    masterByUid.set(seriesUid, master);
    for (const ev of group) items.push(toImportedItem(ev, seriesUid));
  }

  const assembleOpts: AssembleOptions = {
    now,
    resolveCalendarId: () => opts.calendarId,
    resolveLabelIds: () => [],
    resolveTopicIds: () => [],
    emptySummaryTitle: "",
  };
  const assembly = assembleImport(items, assembleOpts);

  // Stamp external identity (assembleImport tags these as an ICS import) and,
  // for our own tasks, stamp the completion time + collect subtask extras.
  const extras: TaskExtrasEntry[] = [];
  for (const task of assembly.tasks) {
    const master = task.ical_uid ? masterByUid.get(task.ical_uid) : undefined;
    if (!master) continue;
    task.external_id = master.id;
    task.etag = master.etag ?? null;
    task.source = "google";
    task.external_calendar_id = opts.externalCalendarId;
    if (!task.is_event && task.status === "done" && !task.completed_at) {
      task.completed_at = isoString(now());
    }
    if (!task.is_event) {
      const { state } = parseTaskState(master.extendedProperties?.private);
      const { bracketEdits } = parseManagedBlock(master.description ?? "");
      if (state.subtasks.length || bracketEdits.length) {
        extras.push({ externalId: master.id, knownSubtasks: state.subtasks, bracketEdits });
      }
    }
  }

  return { assembly, tombstones, looseInstances, extras };
}

function toImportedItem(ev: GoogleEvent, seriesUid: string): ImportedItem {
  const start = googleDate(ev.start);
  const end = googleDate(ev.end);
  const rec = parseRecurrence(ev.recurrence);
  // Task-ness = a title marker OR our machine state (tcTask). A task's done-state
  // comes from the TITLE marker; priority/deadline come from `tcState` (the machine
  // channel), with a legacy block-text fallback for pre-`tcState` events.
  const title = parseGoogleTitle(ev.summary ?? "");
  const ext = parseTaskState(ev.extendedProperties?.private);
  const isTask = title.isTask || ext.isTask;
  // Only task events have a managed block to strip; plain events keep verbatim notes.
  const userNotes = isTask ? parseManagedBlock(ev.description ?? "").userNotes : ev.description ?? "";
  const legacy = isTask && !ext.hasState ? parseLegacyExtras(ev.description ?? "") : null;
  const priority = isTask ? (ext.hasState ? ext.state.priority : legacy?.priority ?? "none") : "none";
  const deadline = isTask ? (ext.hasState ? ext.state.deadline : legacy?.deadline ?? null) : null;
  return {
    kind: "event",
    uid: seriesUid,
    summary: title.title,
    description: userNotes,
    location: ev.location ?? "",
    start,
    end,
    deadlineAt: deadline ? `${deadline}T00:00:00.000Z` : null,
    durationMinutes: null, // assembleImport derives it from start/end
    allDay: start?.allDay ?? false,
    rrule: rec.rrule,
    until: rec.until,
    recurrenceId: ev.recurringEventId ? googleDate(ev.originalStartTime) ?? start : null,
    exdates: rec.exdates,
    reminders: remindersOf(ev),
    priority,
    color: colorIdToHex(ev.colorId),
    isEvent: !isTask,
    status: isTask ? (title.done ? "done" : "open") : statusOf(ev.status),
    categories: [],
    topics: [],
    calendarName: null,
    tz: ev.start?.timeZone ?? null,
  };
}

function toLooseInstance(ev: GoogleEvent): LooseInstance {
  const start = googleDate(ev.start);
  const orig = googleDate(ev.originalStartTime) ?? start;
  return {
    masterExternalId: ev.recurringEventId!,
    recurrenceIdRef: orig?.iso ?? "",
    cancelled: ev.status === "cancelled",
    patch: {
      due_at: start?.iso ?? null,
      duration_minutes: durationOf(start, googleDate(ev.end)),
      all_day: start?.allDay ?? false,
      title: ev.summary !== undefined ? parseGoogleTitle(ev.summary).title : null,
      notes: ev.description ? parseManagedBlock(ev.description).userNotes || null : null,
      location: ev.location ?? null,
    },
  };
}

function parseGoogleTitle(raw: string): ReturnType<typeof parseTaskTitle> {
  const parsed = parseTaskTitle(raw);
  if (!parsed.isTask && isGoogleUntitledSummary(parsed.title)) return { ...parsed, title: "" };
  return parsed;
}

function isGoogleUntitledSummary(title: string): boolean {
  return title.trim().toLowerCase() === "(untitled)";
}

/** A Google start/end point → a normalized UTC instant value. */
function googleDate(gdt?: GoogleEventDateTime): IcsDateValue | null {
  if (!gdt) return null;
  if (gdt.date) {
    const [y, mo, d] = gdt.date.split("-").map(Number);
    return { iso: new Date(Date.UTC(y!, mo! - 1, d!)).toISOString(), allDay: true, tz: null };
  }
  if (gdt.dateTime) {
    return { iso: new Date(gdt.dateTime).toISOString(), allDay: false, tz: gdt.timeZone ?? null };
  }
  return null;
}

function durationOf(start: IcsDateValue | null, end: IcsDateValue | null): number | null {
  if (!start || !end) return null;
  return Math.max(0, Math.round((new Date(end.iso).getTime() - new Date(start.iso).getTime()) / 60_000));
}

function remindersOf(ev: GoogleEvent): number[] {
  // useDefault=true means "the calendar's default reminders", which the events
  // API doesn't enumerate — documented gap, mapped to no explicit reminder.
  if (!ev.reminders || ev.reminders.useDefault) return [];
  return (ev.reminders.overrides ?? []).map((o) => Math.max(0, o.minutes));
}

function statusOf(s: GoogleEvent["status"]): Status {
  return s === "cancelled" ? "cancelled" : "open";
}

/** Split a Google `recurrence` array into our rrule/until/exdates. RDATE ignored. */
function parseRecurrence(lines?: string[]): {
  rrule: string | null;
  until: string | null;
  exdates: IcsDateValue[];
} {
  let rrule: string | null = null;
  let until: string | null = null;
  const exdates: IcsDateValue[] = [];
  for (const line of lines ?? []) {
    const colon = line.indexOf(":");
    if (colon < 0) continue;
    const head = line.slice(0, colon);
    const value = line.slice(colon + 1);
    const name = head.split(";")[0]!.toUpperCase();
    if (name === "RRULE") {
      const kept: string[] = [];
      for (const part of value.split(";")) {
        const [k, val] = part.split("=");
        if (k?.toUpperCase() === "UNTIL" && val) until = parseIcsDate(val).iso;
        else kept.push(part);
      }
      rrule = kept.join(";");
    } else if (name === "EXDATE") {
      const params = parseParams(head);
      for (const tok of value.split(",")) {
        if (tok.trim()) exdates.push(parseIcsDate(tok.trim(), params));
      }
    }
  }
  return { rrule, until, exdates };
}

/** Parse the ";KEY=VAL" params off a property head ("EXDATE;TZID=...;VALUE=DATE"). */
function parseParams(head: string): Record<string, string> {
  const segs = head.split(";");
  const params: Record<string, string> = {};
  for (let i = 1; i < segs.length; i++) {
    const [k, v] = segs[i]!.split("=");
    if (k) params[k.toUpperCase()] = (v ?? "").replace(/^"|"$/g, "");
  }
  return params;
}
